import { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, FileText, Download, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { hasPolicyRichContent, PolicyRichContent, getPolicyDownloadHref } from '@/components/PolicyDocument';
import api from '@/lib/api';

const PolicyViewer = () => {
  const [policy, setPolicy] = useState('');
  const [policyMeta, setPolicyMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fetchPolicy = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Try to get the master policy metadata first
      try {
        const metaResponse = await api.getMasterPolicy();
        if (metaResponse.data && metaResponse.data.content) {
          setPolicyMeta(metaResponse.data);
        }
      } catch (err) {
        // Fallback to legacy policy - this is expected behavior
        console.debug('Using legacy policy:', err.message);
      }
      
      // Get the policy content (API now returns uploaded policy if available)
      const response = await api.getPolicy();
      setPolicy(response.data.policy);
    } catch (err) {
      setError('Failed to load policy document');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchPolicy();
  }, [fetchPolicy]);
  
  return (
    <Layout>
      <div className="p-6 md:p-12 animate-fade-in">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tighter text-zinc-900">
              Procurement Policy
            </h1>
            <p className="mt-2 text-zinc-600">
              Reference document used by AI agents for policy-aligned decisions
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchPolicy}
              className="border-zinc-200"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            {getPolicyDownloadHref(policyMeta) && (
              <a
                href={getPolicyDownloadHref(policyMeta)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-[#0055FF] hover:bg-[#0044CC] rounded-md transition-colors"
              >
                <Download className="w-4 h-4" />
                Download Original
              </a>
            )}
          </div>
        </div>
        
        {/* Policy Document */}
        <div className="bg-white border border-zinc-200">
          <div className="px-6 py-4 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-[#0055FF]" />
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">
                  {policyMeta?.filename || 'Corporate Procurement Policy'}
                </div>
                {policyMeta?.uploaded_at && (
                  <div className="text-xs text-zinc-400 mt-0.5">
                    Uploaded: {new Date(policyMeta.uploaded_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
            {policy && (
              <span className="text-xs text-zinc-400">
                {policy.length.toLocaleString()} characters
              </span>
            )}
          </div>
          
          {/* Content Area */}
          <PolicyContent loading={loading} error={error} policy={policy} policyMeta={policyMeta} />
        </div>
      </div>
    </Layout>
  );
};

// Render content based on loading/error state
function PolicyContent({ loading, error, policy, policyMeta }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-[#0055FF] animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-16 text-zinc-500">{error}</div>;
  }

  if (hasPolicyRichContent(policyMeta)) {
    return (
      <div className="p-6 md:p-8">
        <PolicyRichContent policy={policyMeta} heightClass="h-[70vh]" />
      </div>
    );
  }

  return (
    <ScrollArea className="h-[70vh]">
      <div className="p-6 md:p-8">
        <pre className="font-mono text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">
          {policy}
        </pre>
      </div>
    </ScrollArea>
  );
}

export default PolicyViewer;
